import React, { useState } from "react";
import { Entry } from "contentful";
import { BrandInvestment } from "@tiicker/util/lib/contentful/types";
import styles from "./Investment.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp, faAngleDown } from "@fortawesome/sharp-light-svg-icons";
import WhoWeAre from "components/BrandPage/BrandInvestment/WhoWeAreSection/WhoWeAre";
import Leadership from "components/BrandPage/BrandInvestment/LeadershipSection/Leadership";
import WhyInvest from "components/BrandPage/BrandInvestment/WhyInvestSection/WhyInvest";
import Initiatives from "components/BrandPage/BrandInvestment/InitiativesSection/Initiatives";
import ReadyToInvest from "components/BrandPage/BrandInvestment/ReadyToInvestSection/ReadyToInvest";
import Downloads from "components/BrandPage/BrandInvestment/DownloadSection/Downloads";
import FaqContainer from "components/BrandPage/BrandInvestment/FAQSection/FaqContainer";
import Perks from "components/BrandPage/BrandInvestment/PerkSection/Perks";
import { triggerInvestCTAClick } from "source/analytics-events";

type Props = {
  brandInvestment?: Entry<BrandInvestment>;
  onClose?: () => void;
  isStandalone?: boolean;
  brandName: string;
};

const Investment: React.FC<Props> = ({
  brandInvestment,
  onClose,
  isStandalone = false,
  brandName,
}) => {
  const [isOpen, setIsOpen] = useState(isStandalone);

  if (!brandInvestment?.fields) return null;

  const {
    headerImage,
    quote,
    quoteHex,
    fundingSection,
    investUrl,
    video,
    whoAreWeSection,
    leadershipSection,
    whyInvestSection,
    initiativesSection,
    readyToInvestSection,
    downloadSection,
    faqSection,
  } = brandInvestment.fields;

  // Format number to millions with one decimal place
  const formatToMillions = (num: number) => {
    const millions = (num / 1000000).toFixed(1);
    return millions.endsWith(".0") ? millions.slice(0, -2) : millions;
  };

  const handleToggle = () => {
    if (isStandalone) return;

    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    // If we're closing, scroll to Perks section after a brief delay
    if (!newIsOpen) {
      // Allow time for the content to start collapsing
      setTimeout(() => {
        const perksElement = document.getElementById("Perks");
        if (perksElement) {
          const headerOffset = 100;
          const elementPosition = perksElement.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  return (
    <div className={`container ${styles.Investment__container}`}>
      <div className={styles.Investment}>
        {/* Banner Image */}
        <div className={styles.Investment__imageContainer}>
          <img
            src={headerImage?.fields?.file?.url}
            alt={headerImage?.fields?.title || "Header Image"}
            className={styles.Investment__headerImage}
          />
        </div>

        {/* Overlapping Elements Container */}
        <div className={styles.Investment__content}>
          <div className={styles.Investment__contentWrapper}>
            {/* Quote Section */}
            <div className={styles.Investment__quote}>
              <span
                className={styles.Investment__quote__markOpen}
                style={{ color: quoteHex }}
              >
                "
              </span>
              <p className={styles.Investment__quote__text}>{quote}</p>
              <span
                className={styles.Investment__quote__markClose}
                style={{ color: quoteHex }}
              >
                "
              </span>
            </div>

            {/* Funding Box */}
            <div
              className={styles.Investment__funding}
              style={{ borderColor: fundingSection?.fields?.outlineHex }}
            >
              <h3 className={styles.Investment__funding__title}>
                {fundingSection?.fields?.title}
              </h3>
              <p className={styles.Investment__funding__description}>
                {fundingSection?.fields?.description}
              </p>

              {/* Funding Progress (if showFunds is true) */}
              {fundingSection?.fields?.showFunds && (
                <div className={styles.Investment__funding__progress}>
                  <div className={styles.Investment__funding__progress__bar}>
                    <div
                      className={styles.Investment__funding__progress__fill}
                      style={{
                        width: `${
                          (fundingSection.fields.currentFunds /
                            fundingSection.fields.totalFunds) *
                          100
                        }%`,
                        backgroundColor: fundingSection.fields.buttonHex,
                      }}
                    ></div>
                  </div>
                  <div
                    className={styles.Investment__funding__progress__numbers}
                  >
                    <span>
                      <span
                        className={styles.Investment__funding__progress__bold}
                      >
                        ${formatToMillions(fundingSection.fields.currentFunds)}{" "}
                        million
                      </span>{" "}
                      of{" "}
                      <span
                        className={styles.Investment__funding__progress__bold}
                      >
                        ${formatToMillions(fundingSection.fields.totalFunds)}{" "}
                        million
                      </span>{" "}
                      max
                    </span>
                  </div>
                </div>
              )}

              {/* CTA Button */}
              <button
                className={styles.Investment__funding__button}
                style={{
                  backgroundColor: fundingSection?.fields?.buttonHex,
                  color: fundingSection?.fields?.buttonCopyHex,
                }}
                onClick={() => {
                  triggerInvestCTAClick(brandName);
                  window.open(investUrl, "_blank", "noopener, noreferrer");
                }}
              >
                {fundingSection?.fields?.buttonCopy}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className={styles.Investment__expandedContent}>
          {video?.fields?.file?.url && (
            <div className={styles.Investment__videoContainer}>
              <video
                className={styles.Investment__video}
                controls
                playsInline
                preload="metadata"
              >
                <source
                  src={`https:${video.fields.file.url}`}
                  type={video.fields.file.contentType}
                />
                Your browser does not support the video tag.
              </video>
            </div>
          )}
          <WhoWeAre whoWeAre={whoAreWeSection} />
          {brandInvestment.fields.perkSection && (
            <Perks
              perkSection={brandInvestment.fields.perkSection}
              symbol={brandName}
            />
          )}
          <Leadership leadership={leadershipSection} />
          <WhyInvest whyInvest={whyInvestSection} />
          <Initiatives keyInitiatives={initiativesSection} />
          <ReadyToInvest
            readyToInvest={readyToInvestSection}
            investUrl={investUrl}
          />
          <Downloads downloads={downloadSection} />
          <FaqContainer faqInfo={faqSection} />
        </div>
      )}

      {!isStandalone && (
        <div
          className={`${styles.Investment__iconWrapper} ${
            isOpen
              ? styles.Investment__iconWrapper_expanded
              : styles.Investment__iconWrapper_collapsed
          }`}
        >
          <FontAwesomeIcon
            className={styles.Investment__icon}
            icon={isOpen ? faAngleUp : faAngleDown}
            style={{
              color: fundingSection?.fields?.buttonHex,
            }}
            onClick={handleToggle}
          />
        </div>
      )}
    </div>
  );
};

export default Investment;
